import { useEffect, useState } from 'react';
import {
	AppBar,
	Box,
	Button,
	Container,
	Divider,
	Drawer,
	IconButton,
	List,
	ListItemButton,
	ListItemText,
	Toolbar,
	Tooltip,
	useMediaQuery,
	useScrollTrigger,
	useTheme,
} from '@mui/material';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate, useLocation } from 'react-router-dom';
import Logo from '../../Components/Logo';
import { scrollToSection } from './constants';

const LandingNav = ({ toggleDarkMode, darkMode }) => {
	const navigate = useNavigate();
	const location = useLocation();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [navHovered, setNavHovered] = useState(false);
	const showBar = useScrollTrigger({ disableHysteresis: true, threshold: 80 });
	const atTop = !showBar;
	const navVisible = showBar || isMobile || navHovered;

	useEffect(() => {
		if (!atTop) {
			setNavHovered(false);
		}
	}, [atTop]);

	const closeDrawer = () => setDrawerOpen(false);

	const handleSectionNav = (id) => {
		closeDrawer();
		if (location.pathname !== '/') {
			navigate(`/#${id}`);
			return;
		}
		scrollToSection(id);
	};

	const handleRouteNav = (path) => {
		closeDrawer();
		navigate(path);
	};

	const navLinks = [
		{ label: 'Sample', action: () => handleSectionNav('sample-demo') },
		{ label: 'Follow-ups', action: () => handleSectionNav('follow-ups') },
		{
			label: 'Pricing',
			action: () =>
				location.pathname === '/'
					? handleSectionNav('pricing')
					: handleRouteNav('/pricing'),
		},
		{ label: 'Reviews', action: () => handleSectionNav('testimonials') },
	];

	return (
		<>
			<Box
				onMouseEnter={() => atTop && !isMobile && setNavHovered(true)}
				onMouseLeave={() => atTop && !isMobile && setNavHovered(false)}
				sx={{
					position: 'fixed',
					top: 0,
					left: 0,
					right: 0,
					zIndex: theme.zIndex.appBar,
					height: atTop && !isMobile ? (navVisible ? 72 : 28) : 'auto',
					pointerEvents: atTop && !isMobile ? 'auto' : 'none',
				}}
			>
				<AppBar
					position="fixed"
					elevation={navVisible ? 2 : 0}
					sx={{
						bgcolor: 'background.paper',
						color: 'text.primary',
						borderBottom: '1px solid',
						borderColor: navVisible ? 'divider' : 'transparent',
						transform: navVisible ? 'translateY(0)' : 'translateY(-110%)',
						opacity: navVisible ? 1 : 0,
						pointerEvents: navVisible ? 'auto' : 'none',
						transition: theme.transitions.create(
							['transform', 'opacity', 'box-shadow', 'border-color'],
							{
								duration: theme.transitions.duration.enteringScreen,
								easing: theme.transitions.easing.easeInOut,
							},
						),
					}}
				>
					<Container maxWidth="lg">
						<Toolbar
							disableGutters
							sx={{ minHeight: { xs: 52, md: 56 }, gap: 0.5 }}
						>
							<Logo
								onClick={() => {
									if (location.pathname !== '/') {
										navigate('/');
										return;
									}
									window.scrollTo({ top: 0, behavior: 'smooth' });
								}}
								sx={{
									width: { xs: 92, md: 112 },
									height: 'auto',
									mr: 'auto',
									cursor: 'pointer',
									flexShrink: 0,
								}}
							/>

							{isMobile ? (
								<>
									<Button
										size="small"
										variant="contained"
										color="warning"
										onClick={() => navigate('/signup')}
										sx={{ minWidth: 'auto', px: 1.5, mr: 0.5 }}
									>
										Trial
									</Button>
									<IconButton
										color="inherit"
										edge="end"
										onClick={() => setDrawerOpen(true)}
										aria-label="Open menu"
									>
										<MenuIcon />
									</IconButton>
								</>
							) : (
								<>
									{navLinks.map((link) => (
										<Button
											key={link.label}
											size="small"
											color="inherit"
											onClick={link.action}
										>
											{link.label}
										</Button>
									))}
									<Tooltip title={darkMode ? 'Light mode' : 'Dark mode'}>
										<IconButton
											onClick={toggleDarkMode}
											size="small"
											color="inherit"
											aria-label={
												darkMode
													? 'Switch to light mode'
													: 'Switch to dark mode'
											}
										>
											{darkMode ? (
												<LightModeIcon fontSize="small" />
											) : (
												<DarkModeIcon fontSize="small" />
											)}
										</IconButton>
									</Tooltip>
									<Button
										size="small"
										variant="contained"
										color="warning"
										onClick={() => navigate('/signup')}
									>
										Start trial
									</Button>
									<Button
										size="small"
										variant="outlined"
										color="primary"
										onClick={() => navigate('/login')}
										sx={{ ml: 0.5 }}
									>
										Login
									</Button>
								</>
							)}
						</Toolbar>
					</Container>
				</AppBar>
			</Box>

			<Drawer
				anchor="right"
				open={drawerOpen}
				onClose={closeDrawer}
				PaperProps={{
					sx: { width: 280, pt: 1 },
				}}
			>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						px: 2,
						pb: 1,
					}}
				>
					<Logo sx={{ width: 88 }} />
					<IconButton onClick={closeDrawer} aria-label="Close menu">
						<CloseIcon />
					</IconButton>
				</Box>
				<Divider />
				<List sx={{ px: 1, py: 1 }}>
					{navLinks.map((link) => (
						<ListItemButton key={link.label} onClick={link.action}>
							<ListItemText primary={link.label} />
						</ListItemButton>
					))}
					<ListItemButton onClick={() => handleRouteNav('/login')}>
						<ListItemText primary="Staff login" />
					</ListItemButton>
				</List>
				<Divider />
				<Box
					sx={{
						px: 2,
						py: 2,
						display: 'flex',
						flexDirection: 'column',
						gap: 1.5,
					}}
				>
					<Button
						fullWidth
						variant="contained"
						color="warning"
						onClick={() => handleRouteNav('/signup')}
					>
						Start free trial
					</Button>
					<Button
						fullWidth
						variant="outlined"
						color="inherit"
						startIcon={darkMode ? <LightModeIcon /> : <DarkModeIcon />}
						onClick={toggleDarkMode}
					>
						{darkMode ? 'Light mode' : 'Dark mode'}
					</Button>
				</Box>
			</Drawer>
		</>
	);
};

export default LandingNav;
